import React from 'react'
import { Network, Package, FolderTree, Truck, PackagePlus, Clock, ArrowDownAZ } from 'lucide-react'
import { Button } from '@sourcepilot/ui/framework/ui/atoms/button'
import { MiniAppProps } from './types'
import { constructUrl, NewTabData, useTabData } from '@sourcepilot/ui/framework/state/session-data';import { AssemblyTree } from '@/ui/molecules/assemblyTree'
import { ModuleTree } from '@/ui/molecules/moduleTree'
import { AssemblyFlat } from '@/ui/molecules/assemblyFlat'
import { MiniAppText } from '@/ui/molecules/mini-app-text'
import { iconMap } from '@sourcepilot/ui/framework/ui/atoms/icons';

import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { productMiniAppStateAtom } from '@/state/rflp-session';import { AssemblyType } from '@/types/enums'
import { AssemblySketch, FunctionalBreakdown, FunctionalSubsystem } from '@/types/workspace'
import { localUuid } from '@sourcepilot/framework/uuid';
import { PDMProduct } from '@sourcepilot/generated/pdm/api/pdmproduct';
import { getRouteForModel } from '@sourcepilot/ui/framework/data/route-resolver';

/**
 * Canonical `contentType` for a model = the first segment of the
 * linkPath the route-resolver currently returns for it. Stays correct
 * across linkPath-override changes (and `?default_view=1` runtime
 * suppression) — hardcoding the segment would silently desync when
 * routing config changes.
 */
function contentTypeForModel(model: string): string {
  const { path } = getRouteForModel(model);
  return path.replace(/^\//, '').split('/')[0];
}

// ---------------------------------------------------------------------------
// List ordering — "recent" (created_at desc) | "alpha" (name). Persisted
// across sessions; applies to both PDM products and RFLP projects.
// ---------------------------------------------------------------------------

type SortMode = 'recent' | 'alpha';
const productSortModeAtom = atomWithStorage<SortMode>('productMiniApp.sortMode', 'recent');

/** Sort sketches without mutating state. `created_at` is a server-set
 *  ISO-8601 string (uniform offset), so lexicographic comparison IS
 *  chronological; missing/epoch timestamps sink to the bottom of the
 *  "recent" ordering, with name as the tiebreak either way. */
function sortSketches<T extends { name: string; created_at?: string | null }>(
  items: T[],
  mode: SortMode,
): T[] {
  const byName = (a: T, b: T) => a.name.localeCompare(b.name);
  if (mode === 'alpha') return [...items].sort(byName);
  return [...items].sort((a, b) => {
    const ta = a.created_at ?? '';
    const tb = b.created_at ?? '';
    if (ta !== tb) return tb.localeCompare(ta);
    return byName(a, b);
  });
}

function SortToggle({
  mode,
  onChange,
}: {
  mode: SortMode;
  onChange: (m: SortMode) => void;
}) {
  const btn = (m: SortMode, Icon: React.ComponentType<any>, title: string) => (
    <button
      type="button"
      title={title}
      onClick={() => onChange(m)}
      className={
        mode === m
          ? 'rounded p-1 bg-[#00ccaa]/15 text-[#00997f]'
          : 'rounded p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100'
      }
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
  return (
    <div className="flex items-center gap-0.5">
      {btn('recent', Clock, 'Sort by most recently created')}
      {btn('alpha', ArrowDownAZ, 'Sort alphabetically')}
    </div>
  );
}

export function ProductMiniApp({ containerWidth }: MiniAppProps) {
  const [ productMiniAppState, setProductMiniAppState ] = useAtom(productMiniAppStateAtom)
  const expandedItems = productMiniAppState.expandedItems
  const { activeTab, navigateToTab } = useTabData();
  const [ hoveredItem, setHoveredItem ] = React.useState<string | null>(null);
  const [ sortMode, setSortMode ] = useAtom(productSortModeAtom);

  const toggleExpanded = (id: string | undefined, label: string | null = null) => {
    if (!id) return;
    const testId = label ? `${id}_${label}` : id;

    if (expandedItems.includes(testId)) {
      setProductMiniAppState(prev => ({
        ...prev,
        expandedItems: prev.expandedItems.filter(item => item !== testId),
      }))
    } else {
      setProductMiniAppState(prev => ({
        ...prev,
        expandedItems: [...prev.expandedItems, testId],
      }))
    }
  }

  const get_icon = (typeName: string, isExpanded: boolean) => {
    const commonStyle = "w-4 h-4 mr-[4px] ml-[2px] rounded";
    const hoverStyle = 'hover:bg-[#00ccaa] hover:shadow-xl';
    const Icn: React.ComponentType<any> | undefined = iconMap[typeName];
    if (!Icn) return null;
    return <Icn className={`${commonStyle} ${hoverStyle}`}  />;
  }

  // Directly render the subsystems without any parent "Modules" node
  const renderSubsystems = (functional_breakdown: FunctionalBreakdown | undefined | null, productName: string) => {
    if (!functional_breakdown || !functional_breakdown.subsystems || functional_breakdown.subsystems.length === 0) return null;

    return (
      <div className="pl-[0px]">
        {/* Directly display subsystems without a parent node */}
        {functional_breakdown.subsystems?.map((child: FunctionalSubsystem) => (
          <ModuleTree
            key={child.id}
            slug={productName}
            containerWidth={containerWidth - 2}
            depth={0}
            funcUnit={child}
          />
        ))}
      </div>
    );
  }

  const hasProducts = productMiniAppState.workspace?.products &&
    productMiniAppState.workspace?.products?.length > 0 &&
    Object.keys(productMiniAppState.workspace?.products).length > 0;

  const sortedProducts = sortSketches(
    productMiniAppState.workspace?.products || [],
    sortMode,
  );
  const rflpProjects = sortSketches(
    productMiniAppState.workspace?.rflp_projects || [],
    sortMode,
  );
  const hasRFLPProjects = rflpProjects.length > 0;

  return (
    <div className="pl-[4px]">
      {/* Header with sort toggle. The row must be pinned to
          containerWidth (same idiom as the MiniAppText rows below):
          the mini-app's content box can be wider than the visible
          panel, and an unconstrained justify-between pushes the
          toggle past the panel edge, under the neighbouring column. */}
      <div
        className="flex items-center justify-between px-2 py-1"
        style={{ width: containerWidth }}
      >
        <h4 className="scroll-m-20 text-xl font-semibold tracking-tight my-1">Products</h4>
        <SortToggle mode={sortMode} onChange={setSortMode} />
        {/* Add Product button - hidden for now */}
        {/* <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            if (productMiniAppState.workspace) {
              navigateToTab({
                id: productMiniAppState.workspace?.id,
                title: "[WORKSPACE]",
                contentType: 'workspace',
                productSlug: 'new_product',
              });
            }
          }}
        >
          <PackagePlus className="h-4 w-4 text-black hover:text-[#00ccaa]" />
        </Button> */}
      </div>

      <div className="border-none">
      {hasProducts ? (
        sortedProducts.map(({id, name, functional_breakdown}) => {
          // product tab
          const tab: NewTabData = {
            id: id,
            title: name,
            contentType: AssemblyType.PRODUCT,
          }
          const pathname = constructUrl(tab);
          const isExpanded = expandedItems.includes(id);

          // render product assembly or (flattened) bom
          return (
          <div key={id} className="m-auto leading-none text-[14px]">
            {/* Product */}
            <div className="flex items-center">
              <button
                onClick={() => toggleExpanded(id)}
                className=""
              >
                {get_icon('product', isExpanded)}
              </button>

              <button
                className="font-medium cursor-pointer hover:underline flex-grow text-left text-gray-800"
                onClick={() => navigateToTab(tab)}
                onMouseEnter={() => setHoveredItem(pathname)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <MiniAppText
                  className=""
                  containerWidth={containerWidth - 20}
                  isActive={id === activeTab?.id && activeTab.contentType == AssemblyType.PRODUCT}
                  isHovered={pathname === hoveredItem}
                >
                  {name}
                </MiniAppText>
              </button>
            </div>

            {isExpanded && (
              <div className="border-gray-300 border-dotted">
                {/* Render modules directly under product without the [Modules] header */}
                {functional_breakdown && (
                  <div className="pl">
                    {renderSubsystems(functional_breakdown, name)}
                  </div>
                )}
              </div>
            )}
          </div>
          )
        })
      ) : !hasRFLPProjects ? (
        <div className="px-2 py-2 text-sm text-gray-500 italic">
          No Products Yet
        </div>
      ) : null}

      {hasRFLPProjects && (
        <div className="mt-2">
          {rflpProjects.map(({ id, name }) => {
            // Derive contentType from the route-resolver so the mini-app
            // tracks whatever linkPath the framework currently resolves
            // for Project. Must match what `parseUrlToTab`,
            // `EntityLayout.DAGTabTitleUpdater`, and `ProductShell`
            // produce; otherwise the same project ends up with two tabs
            // (one per contentType).
            const contentType = contentTypeForModel('Project');
            const tab: NewTabData = {
              id,
              title: name,
              contentType,
            };
            const pathname = constructUrl(tab);
            return (
              <div key={id} className="m-auto leading-none text-[14px]">
                <div className="flex items-center">
                  <Network className="w-4 h-4 mr-[4px] ml-[2px] rounded text-gray-500" />
                  <button
                    className="font-medium cursor-pointer hover:underline flex-grow text-left text-gray-800"
                    onClick={() => navigateToTab(tab)}
                    onMouseEnter={() => setHoveredItem(pathname)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <MiniAppText
                      className=""
                      containerWidth={containerWidth - 20}
                      isActive={id === activeTab?.id && activeTab.contentType === contentType}
                      isHovered={pathname === hoveredItem}
                    >
                      {name}
                    </MiniAppText>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  )
}

